import React from 'react';
import { ConfigSchema } from './fieldSpec';
import { DEFAULT_LAYOUT, ReusableComponentAttrs, ReusableLayout } from './contract';
import { SizingSpec } from './sizing';
import { assessmentExpectedLossDescriptor } from './assessmentExpectedLoss';

// The editable slice the settings modal works on: config + the assessment/source
// selection + the FE-fetched data. config-only components touch just `config`;
// data-backed ones (e.g. expected loss) also set sourceId + data.
export interface EditableEnvelope {
  config: Record<string, unknown>;
  sourceId: string | null;
  data: Record<string, unknown> | null;
  layout: ReusableLayout;
}

// A completeness problem surfaced in the editor wrapper. `error` = the component
// won't render meaningfully (e.g. no data source picked); `warning` = it renders
// but something's off (e.g. the chosen figure is unavailable).
export interface ComponentIssue {
  level: 'error' | 'warning';
  message: string;
}

export interface SettingsFormProps {
  descriptor: FeComponentDescriptor;
  value: EditableEnvelope;
  onChange: (next: EditableEnvelope) => void;
}

// Non-generic on purpose (mirrors the runner): config is `Record<string,unknown>`
// at the boundary and each component parses it internally, so the registry map
// stays simple and free of variance gymnastics.
export interface FeComponentDescriptor {
  id: string;
  label: string;
  version: number;
  defaultConfig: Record<string, unknown>;
  // Drives the default settings AutoForm (and the config fields of custom forms).
  configSchema: ConfigSchema;
  // Width presets + edge-drag behaviour. Defaults to full-width, non-resizable.
  sizing: SizingSpec;
  // Optional custom settings form (e.g. an assessment picker). Defaults to AutoForm.
  SettingsForm?: React.ComponentType<SettingsFormProps>;
  // In-editor preview body, rendered inside the debug wrapper.
  EditorBody: React.ComponentType<{ config: Record<string, unknown>; data: Record<string, unknown> | null }>;
  // Completeness check beyond required config fields (e.g. "an assessment must be
  // picked"). Required-field errors are derived from the schema automatically.
  validate?: (env: EditableEnvelope) => ComponentIssue[];
}

export const FE_REGISTRY: Record<string, FeComponentDescriptor> = {
  [assessmentExpectedLossDescriptor.id]: assessmentExpectedLossDescriptor,
};

// Initial attrs for inserting a component (config seeded from defaults, no data
// until the user configures a source).
export function makeInsertAttrs(descriptor: FeComponentDescriptor): ReusableComponentAttrs {
  return {
    component: descriptor.id,
    version: descriptor.version,
    config: { ...descriptor.defaultConfig },
    data: null,
    sourceId: null,
    layout: { ...DEFAULT_LAYOUT },
  };
}

// Errors for any required config field left empty (declared via field-spec).
export function requiredFieldIssues(schema: ConfigSchema, config: Record<string, unknown>): ComponentIssue[] {
  const out: ComponentIssue[] = [];
  for (const f of schema) {
    if (f.type === 'boolean') continue;
    if (!f.required) continue;
    const v = config[f.key];
    const empty = v === undefined || v === null || (typeof v === 'string' && v.trim() === '');
    if (empty) out.push({ level: 'error', message: `${f.label} is required` });
  }
  return out;
}

// All completeness issues for a component: required-field errors (from the
// schema) plus the descriptor's own validate().
export function collectIssues(descriptor: FeComponentDescriptor, env: EditableEnvelope): ComponentIssue[] {
  return [...requiredFieldIssues(descriptor.configSchema, env.config), ...(descriptor.validate?.(env) ?? [])];
}

export function worstIssueLevel(issues: ComponentIssue[]): 'error' | 'warning' | null {
  if (issues.some((i) => i.level === 'error')) return 'error';
  if (issues.some((i) => i.level === 'warning')) return 'warning';
  return null;
}
